import { useState, type FormEvent } from "react";
import Reveal from "@/components/base/Reveal";
import { previewSegments } from "@/mocks/criacao-sites";

const SUBMIT_ADDR = "https://readdy.ai/api/form/datvt89ggrtvtk1bg7u0";

const FIELD_CLASS =
  "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100";
const LABEL_CLASS = "mb-1.5 block text-xs font-semibold text-foreground-700";

export default function PreviewForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = String(value);
      if (text.trim() === "") return;
      payload.append(key, text);
    });

    setStatus("submitting");
    setFormError("");
    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "Não foi possível enviar agora. Tente novamente em instantes."
        );
      }
    } catch {
      setStatus("error");
      setFormError("Não foi possível enviar agora. Verifique sua conexão e tente novamente.");
    }
  };

  return (
    <section id="previa" className="scroll-mt-24 bg-background-50 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <Reveal>
          <div className="grid grid-cols-1 gap-8 rounded-2xl border border-background-200 bg-background-100/60 p-6 md:p-10 lg:grid-cols-[0.95fr_1.05fr] lg:gap-12">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-background-50">
                <i className="ri-image-add-line" aria-hidden="true"></i>
                Prévia sem custo
              </span>
              <h2 className="mt-5 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
                Quer ver como sua empresa poderia ficar na internet?
              </h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-600">
                Solicite uma prévia inicial sem custo e conheça uma proposta de design criada para o
                seu negócio.
              </p>

              <ul className="mt-6 flex flex-col gap-3">
                {[
                  "Uma proposta visual inicial para o seu segmento",
                  "Atendimento personalizado com especialista",
                  "Sem custo e sem compromisso de contratação",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-foreground-700">
                    <i
                      className="ri-checkbox-circle-line mt-0.5 text-lg text-secondary-600"
                      aria-hidden="true"
                    ></i>
                    {item}
                  </li>
                ))}
              </ul>

              <div className="mt-6 flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
                <i className="ri-information-line text-lg text-primary-600" aria-hidden="true"></i>
                <p className="text-xs leading-relaxed text-foreground-600">
                  A solicitação da prévia não gera obrigação de contratação.
                </p>
              </div>
            </div>

            <div>
              {status === "success" ? (
                <div className="flex h-full flex-col items-center justify-center rounded-xl border border-secondary-200 bg-secondary-50 p-8 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-500 text-background-50">
                    <i className="ri-check-line text-2xl" aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                    Solicitação enviada!
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600">
                    Recebemos os dados da sua empresa. Em breve a NXHOST entra em contato para preparar
                    a sua prévia.
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    Enviar outra solicitação
                  </button>
                </div>
              ) : (
                <form
                  id="previa-site-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6"
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className={LABEL_CLASS} htmlFor="previa-name">
                        Nome
                      </label>
                      <input
                        id="previa-name"
                        name="name"
                        type="text"
                        required
                        placeholder="Seu nome"
                        className={FIELD_CLASS}
                      />
                    </div>
                    <div>
                      <label className={LABEL_CLASS} htmlFor="previa-company">
                        Nome da empresa
                      </label>
                      <input
                        id="previa-company"
                        name="company"
                        type="text"
                        placeholder="Nome da sua empresa"
                        className={FIELD_CLASS}
                      />
                    </div>
                    <div>
                      <label className={LABEL_CLASS} htmlFor="previa-whatsapp">
                        WhatsApp
                      </label>
                      <input
                        id="previa-whatsapp"
                        name="whatsapp"
                        type="tel"
                        required
                        placeholder="(00) 00000-0000"
                        className={FIELD_CLASS}
                      />
                    </div>
                    <div>
                      <label className={LABEL_CLASS} htmlFor="previa-email">
                        E-mail
                      </label>
                      <input
                        id="previa-email"
                        name="email"
                        type="email"
                        required
                        placeholder="voce@empresa.com.br"
                        className={FIELD_CLASS}
                      />
                    </div>
                    <div>
                      <label className={LABEL_CLASS} htmlFor="previa-site">
                        Site atual (opcional)
                      </label>
                      <input
                        id="previa-site"
                        name="current_site"
                        type="text"
                        placeholder="www.suaempresa.com.br"
                        className={FIELD_CLASS}
                      />
                    </div>
                    <div>
                      <label className={LABEL_CLASS} htmlFor="previa-instagram">
                        Instagram (opcional)
                      </label>
                      <input
                        id="previa-instagram"
                        name="instagram"
                        type="text"
                        placeholder="@suaempresa"
                        className={FIELD_CLASS}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={LABEL_CLASS} htmlFor="previa-segment">
                        Qual é o segmento da sua empresa?
                      </label>
                      <input
                        id="previa-segment"
                        name="segment"
                        type="text"
                        placeholder="Ex.: clínica, indústria, loja de roupas..."
                        className={FIELD_CLASS}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={LABEL_CLASS} htmlFor="previa-goal">
                        O que você gostaria de melhorar?
                      </label>
                      <select
                        id="previa-goal"
                        name="goal"
                        required
                        defaultValue=""
                        className={FIELD_CLASS}
                      >
                        <option value="" disabled>
                          Selecione uma opção
                        </option>
                        {previewSegments.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className={LABEL_CLASS} htmlFor="previa-details">
                        Detalhes do projeto (opcional)
                      </label>
                      <textarea
                        id="previa-details"
                        name="details"
                        rows={3}
                        maxLength={500}
                        placeholder="Conte um pouco sobre o seu negócio e o que você espera do site."
                        className={`${FIELD_CLASS} resize-none`}
                      ></textarea>
                    </div>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="nx-honeypot-field"
                  />

                  {status === "error" ? (
                    <p className="mt-4 rounded-md border border-accent-200 bg-accent-50 px-3.5 py-2.5 text-xs text-accent-800">
                      {formError}
                    </p>
                  ) : null}

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {status === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-lg" aria-hidden="true"></i>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill text-lg" aria-hidden="true"></i>
                        Quero receber uma prévia
                      </>
                    )}
                  </button>
                  <p className="mt-3 text-center text-[11px] text-foreground-500">
                    A solicitação da prévia não gera obrigação de contratação.
                  </p>
                </form>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}